import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore'
import toast from 'react-hot-toast'

const Schedule = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [lessons, setLessons] = useState([])
  const [userRole, setUserRole] = useState(null)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedDay, setSelectedDay] = useState('')
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    className: '',
    note: '',
    startTime: '09:00',
    endTime: '10:00'
  })

  const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']

  useEffect(() => {
    if (user) {
      loadUserRole()
      loadLessons()
    }
  }, [user])

  const loadUserRole = async () => {
    try {
      const userDoc = await getDocs(query(
        collection(db, 'users'),
        where('__name__', '==', user.uid)
      ))
      if (!userDoc.empty) {
        setUserRole(userDoc.docs[0].data().role)
      }
    } catch (error) {
      console.error('Error loading user role:', error)
    }
  }

  const loadLessons = () => {
    try {
      const lessonsRef = collection(db, 'users', user.uid, 'schedule')
      const unsubscribe = onSnapshot(lessonsRef, (snapshot) => {
        const lessonsData = []
        snapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data()
          })
        })
        
        // Günlere göre sırala
        lessonsData.sort((a, b) => {
          const dayOrder = DAYS.indexOf(a.day) - DAYS.indexOf(b.day)
          if (dayOrder !== 0) return dayOrder
          return a.startTime.localeCompare(b.startTime)
        })
        
        setLessons(lessonsData)
        setLoading(false)
      }, (error) => {
        console.error('Error loading lessons:', error)
        setLoading(false)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error setting up lessons listener:', error)
      setLoading(false)
    }
  }

  const handleAddLesson = (day) => {
    setSelectedDay(day)
    setFormData({
      name: '',
      className: '',
      note: '',
      startTime: '09:00',
      endTime: '10:00'
    })
    setShowAddDialog(true)
  }

  const handleEditLesson = (lesson) => {
    setSelectedLesson(lesson)
    setFormData({
      name: lesson.name || '',
      className: lesson.className || '',
      note: lesson.note || '',
      startTime: lesson.startTime || '09:00',
      endTime: lesson.endTime || '10:00'
    })
    setShowEditDialog(true)
  }

  const handleDeleteLesson = async (lesson) => {
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'schedule', lesson.id))
      toast.success('Ders başarıyla silindi')
    } catch (error) {
      console.error('Error deleting lesson:', error)
      toast.error('Ders silinirken hata oluştu')
    }
  }

  const handleSaveLesson = async () => {
    if (!formData.name.trim()) {
      toast.error('Ders adı boş olamaz')
      return
    }

    if (formData.startTime >= formData.endTime) {
      toast.error('Bitiş saati başlangıç saatinden sonra olmalıdır')
      return
    }

    try {
      const lessonData = {
        name: formData.name.trim(),
        day: selectedDay,
        startTime: formData.startTime,
        endTime: formData.endTime
      }

      if (userRole === 'teacher' && formData.className.trim()) {
        lessonData.className = formData.className.trim()
      }

      if (formData.note.trim()) {
        lessonData.note = formData.note.trim()
      }

      if (showEditDialog && selectedLesson) {
        // Düzenleme
        await updateDoc(doc(db, 'users', user.uid, 'schedule', selectedLesson.id), lessonData)
        toast.success('Ders başarıyla güncellendi')
      } else {
        // Yeni ekleme
        await addDoc(collection(db, 'users', user.uid, 'schedule'), lessonData)
        toast.success('Ders başarıyla eklendi')
      }

      setShowAddDialog(false)
      setShowEditDialog(false)
      setSelectedLesson(null)
    } catch (error) {
      console.error('Error saving lesson:', error)
      toast.error('Ders kaydedilirken hata oluştu')
    }
  }

  const getLessonsByDay = (day) => {
    return lessons.filter(lesson => lesson.day === day)
  }

  const formatTime = (time) => {
    return time
  }

  const getDayIcon = (day) => {
    const icons = {
      'Pazartesi': '📅',
      'Salı': '📅',
      'Çarşamba': '📅',
      'Perşembe': '📅',
      'Cuma': '📅',
      'Cumartesi': '📅',
      'Pazar': '📅'
    }
    return icons[day] || '📅'
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Ders Programı
            </Typography>
            <IconButton color="inherit">
              <NotificationsIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ders Programı
          </Typography>
          <IconButton color="inherit">
            <NotificationsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {lessons.length === 0 ? (
          <Alert severity="info" sx={{ textAlign: 'center', mb: 3 }}>
            Henüz ders programı oluşturulmamış
          </Alert>
        ) : null}

        {/* Days List */}
        {DAYS.map((day) => {
          const dayLessons = getLessonsByDay(day)
          return (
            <Card key={day} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                {/* Day Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {getDayIcon(day)} {day}
                    </Typography>
                    {dayLessons.length > 0 && (
                      <Chip
                        label={`${dayLessons.length} ders`}
                        size="small"
                        color="secondary"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddLesson(day)}
                  >
                    Ders Ekle
                  </Button>
                </Box>

                {/* Lessons List */}
                {dayLessons.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Bu gün için henüz ders eklenmemiş
                  </Typography>
                ) : (
                  <List>
                    {dayLessons.map((lesson, index) => (
                      <React.Fragment key={lesson.id}>
                        <ListItem sx={{ px: 0 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {lesson.name}
                              </Typography>
                              <Box display="flex" gap={1}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditLesson(lesson)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteLesson(lesson)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" mb={1}>
                              <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                              {formatTime(lesson.startTime)} - {formatTime(lesson.endTime)}
                            </Typography>
                            
                            {lesson.className && (
                              <Typography variant="body2" color="primary" mb={1}>
                                Sınıf: {lesson.className}
                              </Typography>
                            )}
                            
                            {lesson.note && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Not: {lesson.note}
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                        {index < dayLessons.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )
        })}
      </Container>

      {/* Add/Edit Lesson Dialog */}
      <Dialog
        open={showAddDialog || showEditDialog}
        onClose={() => {
          setShowAddDialog(false)
          setShowEditDialog(false)
          setSelectedLesson(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showEditDialog ? `${selectedLesson?.day} için Ders Düzenle` : `${selectedDay} için Ders Ekle`}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Ders Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          
          {userRole === 'teacher' && (
            <TextField
              fullWidth
              label="Sınıf Adı"
              value={formData.className}
              onChange={(e) => setFormData({ ...formData, className: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          
          <TextField
            fullWidth
            label="Not (İsteğe bağlı)"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Başlangıç Saati"
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              fullWidth
              label="Bitiş Saati"
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddDialog(false)
            setShowEditDialog(false)
            setSelectedLesson(null)
          }}>
            İptal
          </Button>
          <Button onClick={handleSaveLesson} variant="contained">
            {showEditDialog ? 'Kaydet' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default Schedule
